/**
 * Native tool (SMSWiser) performance for one drop. One button per dimension table
 * (Delivery / A/B / Batches / Short-links) — no scrolling — plus an optional
 * per-message drill-down. Reused by the campaign stats section and the drop page.
 * For Media Devoted | Created By Ciro Autiero
 */

import { type FC, useEffect, useMemo, useState } from 'react';
import { BarChart3, MessageSquare, RefreshCw } from 'lucide-react';

import { Button, LoadingPanel } from '@/components/ui';
import { getApiErrorMessage } from '@/lib/utils';
import { useToolDropStats } from '@/hooks/smswiser/tool-stats';

import {
  TOOL_DIMENSIONS,
  toGenericStatsRowFromToolRow,
  toToolMetricRecord,
} from './shared/tool-columns';
import { TOOL_GROUP_OPTIONS } from './shared/tool-grouping';
import { InfoHint } from './shared/InfoHint';
import { StatsSourceToggle } from './StatsSourceToggle';
import { ToolStatsTable } from './components/ToolStatsTable';
import { ToolMessagesPanel } from './components/ToolMessagesPanel';

/** One-line explanation shown under the tab row, per active dimension. */
const DIMENSION_CAPTIONS: Record<string, string> = {
  sms: 'Send & delivery only — the money columns are N/A here (SMSWiser’s delivery feed carries no cost/revenue). Use “Show per-message detail” below for real delivered / not-delivered status.',
  abTesting:
    'One row per sender × text variant. Sent, clicks, CTR and cost are real; revenue & conversions stay 0 until Binom conversion postbacks are configured — so profit and ROI read negative.',
  batch: 'Performance broken down per send batch.',
  shortlink: 'Performance grouped by the short-link domain used in the message.',
};

interface ToolStatsViewProps {
  uniqueId: string;
  enabled?: boolean;
}

export const ToolStatsView: FC<ToolStatsViewProps> = ({ uniqueId, enabled = true }) => {
  const { data, isLoading, isError, error, isFetching, refetch } = useToolDropStats(
    uniqueId,
    enabled,
  );
  const [activeDim, setActiveDim] = useState<string>('');
  const [showMessages, setShowMessages] = useState(false);

  const blocks = data?.blocks ?? [];
  // Only dimensions that actually returned rows get a button.
  const tabs = useMemo(
    () =>
      TOOL_DIMENSIONS.filter((d) =>
        blocks.some((b) => b.dimension === d.dimension && b.rows.length > 0),
      ),
    [blocks],
  );

  useEffect(() => {
    if (tabs.length === 0) {
      if (activeDim) setActiveDim('');
      return;
    }
    if (!tabs.some((t) => t.dimension === activeDim)) setActiveDim(tabs[0]!.dimension);
  }, [tabs, activeDim]);

  if (isLoading) {
    return (
      <LoadingPanel
        size="lg"
        title="Loading native tool stats"
        description="Fetching SMSWiser A/B, batch and short-link performance."
        className="min-h-[240px] border-primary/30 bg-primary/5"
      />
    );
  }

  if (isError) {
    return (
      <div className="flex min-h-[200px] flex-col items-center justify-center gap-3 p-6 text-center">
        <p className="max-w-md text-sm text-destructive">
          Failed to load tool stats. {getApiErrorMessage(error, 'Unknown error')}
        </p>
        <Button variant="outline" size="sm" onClick={() => refetch()}>
          Retry
        </Button>
      </div>
    );
  }

  if (tabs.length === 0) {
    return (
      <div className="px-4 py-8 text-sm text-muted-foreground">
        No native tool stats available for this drop yet.
      </div>
    );
  }

  const active = tabs.find((t) => t.dimension === activeDim) ?? tabs[0]!;
  const activeBlock = blocks.find((b) => b.dimension === active.dimension);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 text-sm font-medium text-foreground">
          <BarChart3 className="h-4 w-4 text-primary" />
          Tool (SMSWiser) performance
          <InfoHint>
            Native SMSWiser numbers, pulled straight from SMSWiser per drop — separate from the
            Voluum/Binom drops table above. Cost &amp; clicks are real; revenue/conversions come from
            Binom postbacks (0 until configured).
          </InfoHint>
        </div>
        <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
          <RefreshCw className={`mr-2 h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      <StatsSourceToggle
        value={active.dimension}
        onChange={setActiveDim}
        options={tabs.map((t) => ({ value: t.dimension, label: t.title }))}
        className="flex-wrap"
      />

      {DIMENSION_CAPTIONS[active.dimension] ? (
        <p className="-mt-1 text-xs leading-relaxed text-muted-foreground">
          {DIMENSION_CAPTIONS[active.dimension]}
        </p>
      ) : null}

      {activeBlock ? (
        <ToolStatsTable
          key={active.dimension}
          rows={activeBlock.rows.map(toGenericStatsRowFromToolRow)}
          totals={toToolMetricRecord(activeBlock.totals)}
          groupOptions={TOOL_GROUP_OPTIONS[active.dimension]}
          firstColumnLabel={active.firstColumnLabel}
          currency={activeBlock.currency ?? undefined}
        />
      ) : null}

      <div className="space-y-3">
        <Button variant="ghost" size="sm" onClick={() => setShowMessages((v) => !v)}>
          <MessageSquare className="mr-2 h-4 w-4" />
          {showMessages ? 'Hide messages' : 'Show per-message detail'}
        </Button>
        {showMessages ? <ToolMessagesPanel uniqueId={uniqueId} /> : null}
      </div>
    </div>
  );
};

export default ToolStatsView;
